
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWishlist } from '../../context/WishlistContext';
import { useCart } from '../../context/CartContext';
import WishlistNavbar from './components/WishlistNavbar';
import WishlistFooter from './components/WishlistFooter';

export default function WishlistPage() {
  const { items, removeFromWishlist, clearWishlist } = useWishlist();
  const { addToCart } = useCart();
  const [removingId, setRemovingId] = useState<string | null>(null);

  const handleRemove = (id: string) => {
    setRemovingId(id);
    setTimeout(() => {
      removeFromWishlist(id);
      setRemovingId(null);
    }, 300);
  };

  const handleAddToCart = (item: typeof items[0]) => {
    addToCart({
      id: item.id,
      name: item.name,
      price: item.price,
      originalPrice: item.originalPrice,
      image: item.image,
      type: item.type,
      description: item.description,
    });
  };

  const handleAddAllToCart = () => {
    items.forEach((item) => {
      addToCart({
        id: item.id,
        name: item.name,
        price: item.price,
        originalPrice: item.originalPrice,
        image: item.image,
        type: item.type,
        description: item.description,
      });
    });
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      class: 'Class',
      kit: 'Kit',
      package: 'Package',
      manual: 'Manual',
      bundle: 'Bundle',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      class: 'bg-emerald-100 text-emerald-700',
      kit: 'bg-amber-100 text-amber-700',
      package: 'bg-rose-100 text-rose-700',
      manual: 'bg-sky-100 text-sky-700',
      bundle: 'bg-violet-100 text-violet-700',
    };
    return colors[type] || 'bg-stone-100 text-stone-700';
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <WishlistNavbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-stone-950 to-stone-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/" className="text-stone-400 hover:text-amber-400 transition-colors text-sm">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-stone-600"></i>
            <span className="text-amber-400 text-sm">Wishlist</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-300 flex items-center justify-center">
                  <i className="ri-heart-3-fill text-2xl text-stone-900"></i>
                </div>
                <div>
                  <h1 className="text-4xl md:text-5xl font-display text-white">My Wishlist</h1>
                  <p className="text-stone-400 mt-1">{items.length} {items.length === 1 ? 'item' : 'items'} saved</p>
                </div>
              </div>
            </div>
            {items.length > 0 && (
              <div className="flex items-center gap-3">
                <button
                  onClick={clearWishlist}
                  className="px-5 py-2.5 border border-stone-700 text-stone-400 text-sm font-medium rounded-full hover:border-rose-500 hover:text-rose-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line mr-2"></i>
                  Clear All
                </button>
                <button
                  onClick={handleAddAllToCart}
                  className="px-6 py-2.5 bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 text-sm font-semibold rounded-full hover:from-amber-300 hover:to-yellow-200 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20"
                >
                  <i className="ri-shopping-cart-line mr-2"></i>
                  Add All to Cart
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Wishlist Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {items.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-32 h-32 rounded-full bg-stone-100 flex items-center justify-center mb-8">
                <i className="ri-heart-line text-6xl text-stone-300"></i>
              </div>
              <h2 className="text-2xl font-serif font-semibold text-stone-900 mb-3">Your wishlist is empty</h2>
              <p className="text-stone-500 max-w-md mb-8">
                Start adding items you love! Browse our collection of cocktail kits, classes, and more.
              </p>
              <Link
                to="/"
                className="px-8 py-3.5 bg-stone-900 text-white font-semibold rounded-full hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-compass-3-line mr-2"></i>
                Explore Products
              </Link>
            </div>
          ) : (
            /* Wishlist Grid */
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.map((item) => (
                <div
                  key={item.id}
                  className={`bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group ${
                    removingId === item.id ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                  }`}
                >
                  {/* Image */}
                  <div className="relative h-56 bg-gradient-to-br from-amber-50 to-stone-50 p-4">
                    {item.badge && (
                      <div className="absolute top-4 left-4 px-3 py-1 bg-stone-900 text-white text-[10px] font-semibold rounded-full uppercase tracking-wider">
                        {item.badge}
                      </div>
                    )}
                    <button
                      onClick={() => handleRemove(item.id)}
                      className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center cursor-pointer hover:bg-rose-50 hover:scale-110 transition-all"
                    >
                      <i className="ri-heart-fill text-rose-500 text-lg"></i>
                    </button>
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-gift-line text-6xl text-stone-200"></i>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2.5 py-0.5 text-[10px] font-semibold rounded-full uppercase tracking-wider ${getTypeColor(item.type)}`}>
                        {getTypeLabel(item.type)}
                      </span>
                      {item.rating && (
                        <div className="flex items-center gap-1 text-xs text-stone-400">
                          <i className="ri-star-fill text-amber-400"></i>
                          <span>{item.rating}</span>
                        </div>
                      )}
                    </div>
                    <h3 className="text-lg font-serif font-semibold text-stone-900 mb-1 line-clamp-1">{item.name}</h3>
                    {item.description && (
                      <p className="text-stone-500 text-sm mb-4 line-clamp-2">{item.description}</p>
                    )}
                    <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                      <div>
                        <span className="text-2xl font-display text-stone-900">${item.price}</span>
                        {item.originalPrice && (
                          <span className="text-stone-300 line-through text-sm ml-2">${item.originalPrice}</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleAddToCart(item)}
                        className="w-11 h-11 rounded-full bg-stone-900 text-white flex items-center justify-center hover:bg-gradient-to-r hover:from-amber-400 hover:to-yellow-300 hover:text-stone-900 transition-all cursor-pointer"
                      >
                        <i className="ri-shopping-cart-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Continue Shopping CTA */}
      {items.length > 0 && (
        <section className="pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-gradient-to-r from-stone-900 to-stone-800 rounded-3xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl font-serif text-white mb-2">Looking for more?</h3>
                <p className="text-stone-400">Discover our full collection of cocktail experiences.</p>
              </div>
              <Link
                to="/"
                className="px-8 py-3.5 bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 font-semibold rounded-full hover:from-amber-300 hover:to-yellow-200 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </section>
      )}

      <WishlistFooter />
    </div>
  );
}
